import { Button, Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type SdmModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/SdmModuleWithPermissionsSpecs';
import {
    type OnImportedHandler,
    useExcelUploadModal,
} from '../../../../components/sdmSalesPersonCodeMappingImportExport';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import { useBankIntegrationDrawer } from './BankIntegrations/BankIntegrationDrawer';
import HelpersContext from './HelpersContext';
import { useSalesForceFormDrawer } from './SalesForces/SaleForcesFormDrawer';
import SdmModuleAction from './SdmModuleAction';
import SdmModuleDetailsInner from './SdmModuleDetailsInner';

export type SdmModulePageProps = { module: SdmModuleWithPermissionsSpecsFragment };

export enum HeaderTabs {
    SalesForce = 'salesForce',
    BankIntegrations = 'bankIntegrations',
    Authorization = 'authorization',
    SalesPersonCodeMappings = 'salesPersonCodeMappings',
}

const SdmModulePage = ({ module }: SdmModulePageProps) => {
    const { t } = useTranslation(['sdmModuleDetails']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.SalesForce);
    const salesForceDrawer = useSalesForceFormDrawer();
    const bankIntegrationDrawer = useBankIntegrationDrawer();

    const onMappingsImportedRef = useRef<OnImportedHandler | null>(null);
    const excelModal = useExcelUploadModal(module.id, onMappingsImportedRef);

    const helpers = useMemo(
        () => ({
            setOnImported: (onImported: OnImportedHandler) => {
                onMappingsImportedRef.current = onImported;
            },
        }),
        []
    );
    const screens = Grid.useBreakpoint();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('sdmModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('sdmModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    // submit button for updating sales peron code mappings
    const footer = [
        activeTab === HeaderTabs.SalesPersonCodeMappings && (
            <Button form="updateSalesPersonCodeMappingForm" htmlType="submit" type="primary">
                {t('sdmModuleDetails:salesPersonCodeMappings.buttons.update')}
            </Button>
        ),
    ].filter(Boolean);

    return (
        <ConsolePageWithHeader
            content={content}
            extra={
                <SdmModuleAction
                    module={module}
                    openBankIntegrationDrawer={bankIntegrationDrawer.open}
                    openImportMappingsModal={excelModal.open}
                    openSaleForceDrawer={salesForceDrawer.open}
                    permissions={permissions}
                    setActiveTab={setActiveTab}
                />
            }
            footer={footer}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}>
                        <Tabs.TabPane key={HeaderTabs.SalesForce} tab={t('sdmModuleDetails:tabs.salesForce.title')} />
                        <Tabs.TabPane
                            key={HeaderTabs.BankIntegrations}
                            tab={t('sdmModuleDetails:tabs.bankIntegrations.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.Authorization}
                            tab={t('sdmModuleDetails:tabs.authorization.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.SalesPersonCodeMappings}
                            tab={t('sdmModuleDetails:tabs.salesPersonCodeMappings.title')}
                        />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('sdmModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <HelpersContext.Provider value={helpers}>
                <SdmModuleDetailsInner
                    activeTab={activeTab}
                    module={module}
                    openBankIntegrationDrawer={bankIntegrationDrawer.open}
                />
            </HelpersContext.Provider>
            {salesForceDrawer.render(module)}
            {bankIntegrationDrawer.render(module)}
            {excelModal.render()}
        </ConsolePageWithHeader>
    );
};

export default SdmModulePage;
